import { CurrencyEnum } from '../../enums';
import lang from '../../language';
import { DynamicFilterType } from '../../services/filter/types';
import { SimplePagedResponse } from '../common-response-types';
import { NullableNumber, NullableString } from '../../util/types';
import { BasePageableRequest, OptionalPageAndSizeRequest, OptionalSortingRequest } from '../pageable';

export enum StockExchangeCodeType {
  US_NASDAQ = 'XNYS',
}

export enum MarketCountryCode {
  UnitedStates = 'US',
}

export type BaseSymbolData = {
  symbol: string;
  logoBase64?: string;
  companyName?: string;
  url?: string;
};

export type BaseSymbolAndExchangeData = BaseSymbolData & {
  stockExchangeCode: StockExchangeCodeType;
};

export type SymbolsAndExchangeData = {
 symbols: string[];
 stockExchangeCode: StockExchangeCodeType;
 countryDesignation: MarketCountryCode
};

export type BaseSymbolDataWithName = {
  symbol: string;
  name?: string;
  url?: string;
};

export type BaseSymbolDataWithRequiredName = {
  symbol: string;
  name: string;
  url: string;
};

export type BaseLogoNameAndURL = {
  logo: string;
  name: string;
  url: string;
};

export type SymbolWithExchangeAndOptionalName = BaseSymbolDataWithName & {
  stockExchangeCode: StockExchangeCodeType;
};

export type SymbolAndOptionalExchangeCode = {
  symbol: string;
  stockExchangeCode?: StockExchangeCodeType;
  countryDesignation?: string;
};

export interface AvailableIndexesResponse {
  symbol: string;
  asYahoo: string;
  name: string;
  currency: CurrencyEnum;
  stockExchange: string;
  exchangeShortName: string;
}

export interface PopularStock extends BaseSymbolAndExchangeData {
  popularity: number;
  symbolUniverseSymbol: boolean;
}

export interface PopularStockParams {
  constrained?: boolean;
  count?: number;
}

export interface DividendPayerData extends BaseSymbolDataWithName {
  currentDividendPayment: number;
  dividendYield: number;
  marketCap: number;
  order: number;
  previousDividendPayment: number;
  volume: number;
  close: number;
  dividendRate: number;
}

export type PageableFilterRequest = BasePageableRequest & {
  isSymbolUniverseSymbols?: boolean;
};

export interface HighGrowthCompanyData extends BaseSymbolDataWithName {
  avg30Volume: number;
  epsLast12Months: number;
  growthIndicator: number;
  marketCap: number;
  order: number;
  revenue: number;
}

export interface TopGainerAndLoserData extends SymbolWithExchangeAndOptionalName {
  changePercentage?: string;
  price?: number;
  absoluteChange?: string;
}

export interface EpicMoversResponse {
  topGainers: TopGainerAndLoserData[];
  topLosers: TopGainerAndLoserData[];
}

// This function is required after migrating from React to Vite
// It handles circular dependency issues (temporal dead zone)
const formatLang = (value: unknown, ...args: string[]) => {
  if (typeof value === 'function') return (value as any)(...args);
  const str = String(value ?? '');
  return args.reduce((acc, a, idx) => acc.replace(new RegExp(`@@${idx + 1}`, 'g'), a ?? ''), str);
};

export const TopGainersAndLosersPriceOption = {
  oneToFive: formatLang(lang.commonXtoY, '$1', '$5'),
  fiveToFifteen: formatLang(lang.commonXtoY, '$5', '$15'),
  fifteenToFifty: formatLang(lang.commonXtoY, '$15', '$50'),
  fiftyToHundred: formatLang(lang.commonXtoY, '$50', '$100'),
  overHundred: formatLang(lang.commonOverX, '$100'),
};

export enum EpicMoversTabEnum {
  TopGainers,
  TopLosers,
}

export enum HotGappersTabEnum {
  GapUp,
  GapDown,
}

export enum EpicMoversQueryOption {
  FROM_1_TO_5='FROM_1_TO_5',
  FROM_5_TO_15='FROM_5_TO_15',
  FROM_15_TO_50='FROM_15_TO_50',
  FROM_50_TO_100='FROM_50_TO_100',
  FROM_100='FROM_100',
  CUSTOM='',
}

export interface EpicMoversParamType {
  isSymbolUniverseSymbols?: boolean,
  symbolSet?: StocksSymbolSet
  range: EpicMoversQueryOption
}

export interface EpicMoversCustomParamType {
  symbolSet?: StocksSymbolSet;
  minPrice?: number;
  maxPrice?: number;
  filters?: DynamicFilterType;
}

export interface EpicMoversRequestAndResponse {
  params?: EpicMoversParamType | EpicMoversCustomParamType;
  responseData: EpicMoversResponse;
}

export interface MarketPressureRequest extends OptionalPageAndSizeRequest, OptionalSortingRequest {
  filters?: DynamicFilterType;
  projections?: string;
  symbolSet?: StocksSymbolSet;
}
export enum Week52HighLowStatusType {
  Gainers = 'MORE_THAN_WEEK_52',
  Losers = 'LESS_THAN_WEEK_52',
}

export type ETFParams = OptionalPageAndSizeRequest & {
  symbol?: string;
  filters?: DynamicFilterType;
  projections?: string;
  symbolSet?: string;
}

export type ETF = BaseSymbolAndExchangeData & {
  type: string;
  name?: string;
  avg30Volume?: number;
}

export type ETFDetailParams = OptionalPageAndSizeRequest & {
  filters?: DynamicFilterType;
  projections?: string;
}

export type ETFDetailResponse = {
  symbol: string;
  avg30Volume: number;
}

export interface SectorDetailParams {
  sectorId: number;
  page: number;
  size: number;
}

export interface SectorDetail {
  sector: string;
  sectorId: number;
  content: BaseSymbolAndExchangeData[];
  nextPageToRead: number;
  totalElements?: number;
}

export interface Sector {
  id?: number;
  sector: string;
  symbolCount: number;
  url?: string;
}

export interface NewStockData extends BaseSymbolAndExchangeData {
  changePercentage: string;
  firstClose: number;
  openingChangePercentage: string;
}

export interface Earning {
  currency: string;
  date: string;
  dateConfirmed: string;
  actualEps: number; // TODO: This property was added to fix TS errors. Check all properties against real data.
  eps: string;
  epsEst: string;
  epsPrior: string;
  epsSurprise: string;
  epsSurprisePercent: string;
  epsType: string;
  exchange: string;
  id: string;
  importance: number;
  name: string;
  notes: string;
  period: string;
  reportStatus?: string;
  reported?: boolean;
  upcoming?: boolean;
  periodYear: number;
  revenue: string;
  revenueEst: string;
  revenuePrior: string;
  revenueSurprise: string;
  revenueSurprisePercent: string;
  revenueType: string;
  symbol: string;
  time: string;
  displayTime?: string;
  displayEpsEst?: string;
  isReported?: boolean;
  updated: number;
}

export type CompanyLogoAndNameResponse = BaseSymbolDataWithRequiredName & {
  stockExchangeCode: StockExchangeCodeType;
  countryDesignation: string;
};

export type CompanyLogoAndNameState = BaseLogoNameAndURL & {
  stockExchangeCode: StockExchangeCodeType;
  countryDesignation: string;
};

export enum StocksSymbolSet {
  NONE = 'NONE',
  RETAIL = 'RETAIL',
  ACE = 'ACE',
}

export enum StatementPeriodType {
  ANNUAL = 'ANNUAL',
  QUARTERLY = 'QUARTERLY',
}

export enum RatiosPeriodType {
  ANNUAL = 'ANNUAL',
  QUARTERLY = 'QUARTERLY',
  TTM = 'TTM',
}

export interface BaseStatementSearchParamType {
  symbol: string;
  periodType: StatementPeriodType;
  symbology?: string;
}

export interface BaseDateRangeParamType {
  startDate: string;
  endDate?: NullableString;
}

export interface StatementSearchParamType extends BaseStatementSearchParamType, BaseDateRangeParamType {
}

export interface GapperParamType extends OptionalPageAndSizeRequest, BaseSymbolSetParamType {
  abovePreviousClose: boolean;
}

export interface GapperResponse extends SymbolWithExchangeAndOptionalName{
  changePercent: number;
  abovePreviousClose: boolean;
  symbolType: string;
}

export interface NonWorkingDay {
  date: string;
  description: string;
  halfDay: boolean;
  country: string;
}

export interface MarketHours {
  preMarketStart: string;
  open: string;
  close: string;
  afterMarketEnd: string;
  closeForHalfDay: string;
  afterMarketEndForHalfDay: string;
  country: string;
  timeZone: string;
}

export interface NonWorkingDaysResponse {
  nonWorkingDays: NonWorkingDay[];
  marketHours: MarketHours;
}

export type NonWorkingDaysByCountryResponse = Record<string, NonWorkingDaysResponse>;

export interface BaseSymbolSetParamType {
  symbolSet?: StocksSymbolSet;
}

export interface Week52HighLowParamType
  extends OptionalPageAndSizeRequest, OptionalSortingRequest, BaseSymbolSetParamType {
  changeStatus?: Week52HighLowStatusType;
}

export interface Week52HighLowTopListParamType extends BaseSymbolSetParamType {
  topN: number;
}

export interface BaseRatiosParamType {
  symbol: string;
  periodType?: RatiosPeriodType;
  symbology?: string;
}

export interface RatiosByTypeParamType extends BaseDateRangeParamType, BaseRatiosParamType {}

export interface Week52HighLowResponse extends SymbolWithExchangeAndOptionalName {
  week52High: number;
  week52Low: number;
  lastPrice: number;
  changePercent: number;
  changeStatus: Week52HighLowStatusType;
  symbolType: string;
}

interface SymbolAndSector {
  symbol: string;
  sector: string;
}

export interface OptionalYearAndPeriod {
  calendarYear?: number;
  period?: string;
}

export interface ProfitabilityRatiosResponseElement extends OptionalYearAndPeriod {
  grossProfitMargin: NullableNumber;
  operatingProfitMargin: NullableNumber;
  netProfitMargin: NullableNumber;
  returnOnAssets: NullableNumber;
  returnOnEquity: NullableNumber;
  dividendPayoutRatio?: NullableNumber;
  dividendYield: NullableNumber;
  netIncomePerShare: NullableNumber;
  freeCashFlowPerShare: NullableNumber;
  bookValuePerShare: NullableNumber;
  roic: NullableNumber;
  eps: NullableNumber;
}

export interface LiquidityAndSolvencyRatiosResponseElement extends OptionalYearAndPeriod {
  currentRatio: NullableNumber;
  quickRatio: NullableNumber;
  cashRatio: NullableNumber;
  daysOfSalesOutstanding: NullableNumber;
  debtEquityRatio: NullableNumber;
  interestCoverage: NullableNumber;
  cashFlowToDebtRatio: NullableNumber;
}

export interface ValuationRatiosResponseElement extends OptionalYearAndPeriod{
  priceToBookRatio: NullableNumber;
  priceToSalesRatio: NullableNumber;
  priceEarningsRatio: NullableNumber;
  priceToFreeCashFlowsRatio: NullableNumber;
  priceEarningsToGrowthRatio: NullableNumber;
  enterpriseValueOverEbitda: NullableNumber;
  evToFreeCashFlow: NullableNumber;
}

export interface ProfitabilityRatiosResponse extends SymbolAndSector {
  ratios: ProfitabilityRatiosResponseElement[] | null;
  sectorRatios: ProfitabilityRatiosResponseElement[] | null;
  ttm: ProfitabilityRatiosResponseElement;
  sectorTtm: ProfitabilityRatiosResponseElement;
}

export interface LiquidityAndSolvencyRatiosResponse extends SymbolAndSector {
  ratios: LiquidityAndSolvencyRatiosResponseElement[] | null;
  sectorRatios: LiquidityAndSolvencyRatiosResponseElement[] | null;
  ttm: LiquidityAndSolvencyRatiosResponseElement;
  sectorTtm: LiquidityAndSolvencyRatiosResponseElement;
}

export interface ValuationRatiosResponse extends SymbolAndSector {
  ratios: ValuationRatiosResponseElement[] | null;
  sectorRatios: ValuationRatiosResponseElement[] | null;
  ttm: ValuationRatiosResponseElement;
  sectorTtm: ValuationRatiosResponseElement;
}

export interface Week52HighLowTopListResponse {
  topNHigh: Week52HighLowResponse[];
  topNLow: Week52HighLowResponse[];
}

export interface RatiosResponse {
  profitability: ProfitabilityRatiosResponse;
  liquidityAndSolvency: LiquidityAndSolvencyRatiosResponse;
  valuation: ValuationRatiosResponse;
}

interface CommonStatementType {
  date: string;
  currency: string;
  cik: string;
  fillingDate: string;
  acceptedDate: number;
  link: string;
  finalLink: string;
  symbol: string;
  calendarYear: number;
  period: string;
}

export interface Income extends CommonStatementType {
  revenue: number;
  costOfRevenue: number;
  grossProfit: number;
  grossProfitRatio: number;
  researchAndDevelopmentExpenses: number;
  generalAndAdministrativeExpenses: number;
  sellingAndMarketingExpenses: number;
  sellingGeneralAndAdministrativeExpenses: number;
  otherExpenses: number;
  operatingExpenses: number;
  costAndExpenses: number;
  interestIncome: number;
  interestExpense: number;
  depreciationAndAmortization: number;
  ebitda: number;
  ebitdaRatio: number;
  operatingIncome: number;
  operatingIncomeRatio: number;
  totalOtherIncomeExpensesNet: number;
  incomeBeforeTax: number;
  incomeBeforeTaxRatio: number;
  incomeTaxExpense: number;
  netIncome: number;
  netIncomeRatio: number;
  eps: number;
  epsDiluted: number;
  weightedAverageShsOut: number;
  weightedAverageShsOutDil: number;
}

export interface BalanceSheet extends CommonStatementType {
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  cashAndShortTermInvestments: number;
  netReceivables: number;
  inventory: number;
  otherCurrentAssets: number;
  totalCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  goodwillAndIntangibleAssets: number;
  longTermInvestments: number;
  taxAssets: number;
  otherNonCurrentAssets: number;
  totalNonCurrentAssets: number;
  otherAssets: number;
  totalAssets: number;
  accountPayables: number;
  shortTermDebt: number;
  taxPayables: number;
  deferredRevenue: number;
  otherCurrentLiabilities: number;
  totalCurrentLiabilities: number;
  longTermDebt: number;
  deferredRevenueNonCurrent: number;
  deferredTaxLiabilitiesNonCurrent: number;
  otherNonCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  otherLiabilities: number;
  capitalLeaseObligations: number;
  totalLiabilities: number;
  preferredStock: number;
  commonStock: number;
  retainedEarnings: number;
  accumulatedOtherComprehensiveIncomeLoss: number;
  otherTotalStockholdersEquity: number;
  totalStockholdersEquity: number;
  totalEquity: number;
  totalLiabilitiesAndStockholdersEquity: number;
  minorityInterest: number;
  totalLiabilitiesAndTotalEquity: number;
  totalInvestments: number;
  totalDebt: number;
  netDebt: number;
}

export interface CashFlow extends CommonStatementType {
  netIncome: number;
  depreciationAndAmortization: number;
  deferredIncomeTax: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;
  accountsReceivables: number;
  inventory: number;
  accountsPayables: number;
  otherWorkingCapital: number;
  otherNonCashItems: number;
  netCashProvidedByOperatingActivities: number;
  investmentsInPropertyPlantAndEquipment: number;
  acquisitionsNet: number;
  purchasesOfInvestments: number;
  salesMaturitiesOfInvestments: number;
  otherInvestingActivities: number;
  netCashUsedForInvestingActivities: number;
  debtRepayment: number;
  commonStockIssued: number;
  commonStockRepurchased: number;
  dividendsPaid: number;
  otherFinancingActivities: number;
  netCashUsedProvidedByFinancingActivities: number;
  effectOfForexChangesOnCash: number;
  netChangeInCash: number;
  cashAtEndOfPeriod: number;
  cashAtBeginningOfPeriod: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  freeCashFlow: number;
}

export interface StatementApiResponse {
  income: Income[];
  balanceSheet: BalanceSheet[];
  cashFlow: CashFlow[];
}

export interface SymbolStatementApiResponse extends StatementApiResponse {
  symbol: string;
}

export enum MarketCapCategory {
  MegaLarge = 'MEGA_LARGE',
  Large = 'LARGE',
  Mid = 'MID',
  Small = 'SMALL',
  Penny = 'PENNY',
}

export enum MarketCapRadarTabEnum {
  Gainers,
  Losers,
}

export interface MarketCapData extends BaseSymbolDataWithName {
  marketCap: number;
  price?: number;
  changesPercentage?: number;
  volume?: number;
  volumePercentage?: number;
  avg30Volume?: number;
  currency?: CurrencyEnum;
  countryDesignation?: string;
  stockExchangeCode?: StockExchangeCodeType;
  category?: MarketCapCategory;
}

export interface MarketCapsRequest extends OptionalPageAndSizeRequest, OptionalSortingRequest {
  filters?: DynamicFilterType;
  projections?: string;
  symbolSet?: StocksSymbolSet;
}

/** Payload of `getMarketCaps` - the category is resolved into a `category` filter by the epic. */
export interface MarketCapsParamType extends OptionalPageAndSizeRequest {
  category: MarketCapCategory;
}

/**
 * Gainers and losers of one market cap bucket. The endpoint serves a single sorted page, so the
 * two lists come from two calls sorted in opposite directions and are merged here.
 */
export interface MarketCapsSplitData {
  gainers: SimplePagedResponse<MarketCapData>;
  losers: SimplePagedResponse<MarketCapData>;
}

export interface MarketCapsRequestAndResponse {
  params: MarketCapsParamType;
  responseData: MarketCapsSplitData;
}
